/**
 * Admin-only: what the launch-video tools cost, and how they're behaving.
 *
 * The backend ledgers every ElevenLabs / Gemini call the launcher makes
 * (NORTHSTAR_LAUNCHER_CALLS: billed units and tokens, status, latency) and prices
 * it in code at list rates. ElevenLabs figures are pay-as-you-go equivalents; on
 * a subscription the marginal cost is zero until the plan's credits run out.
 */
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLauncherCosts } from "@/lib/useAdmin";
import type { LauncherCostRow } from "@/lib/types";

const OPERATION_LABELS: Record<string, string> = {
  narration: "narration",
  transcription: "transcription",
  sound_effect: "sound effects",
  music: "music",
  review: "reviews",
  image: "images",
};

function usd(value: number): string {
  return value >= 100 ? `$${value.toFixed(0)}` : `$${value.toFixed(value >= 1 ? 2 : 4)}`;
}

interface Bucket {
  label: string;
  calls: number;
  errors: number;
  cost: number;
  unpriced: boolean;
  latencyWeighted: number;
}

function rollup(rows: LauncherCostRow[], label: (row: LauncherCostRow) => string): Bucket[] {
  const buckets = new Map<string, Bucket>();
  for (const row of rows) {
    const key = label(row);
    const b = buckets.get(key) ?? { label: key, calls: 0, errors: 0, cost: 0, unpriced: false, latencyWeighted: 0 };
    b.calls += row.calls;
    b.errors += row.errors;
    b.latencyWeighted += row.avg_latency_ms * row.calls;
    if (row.cost_usd == null) b.unpriced = true;
    else b.cost += row.cost_usd;
    buckets.set(key, b);
  }
  return [...buckets.values()].sort((a, b) => b.cost - a.cost);
}

function BucketTable({ title, rows, latency }: { title: string; rows: Bucket[]; latency?: boolean }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">{title}</span>
      {rows.map((row) => (
        <div key={row.label} className="flex items-baseline justify-between gap-3 font-sans text-xs">
          <span className="truncate text-bw-ink">{row.label}</span>
          <span className="shrink-0 text-bw-ink-muted">
            {row.calls} call{row.calls === 1 ? "" : "s"}
            {row.errors > 0 && <span className="text-bw-danger"> · {row.errors} failed</span>}
            {latency && row.calls > 0 && <> · avg {(row.latencyWeighted / row.calls / 1000).toFixed(1)} s</>} ·{" "}
            <span className="font-semibold text-bw-navy">{row.unpriced ? `≥ ${usd(row.cost)}` : usd(row.cost)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export function AdminLauncherCosts() {
  const { data, isLoading, isError } = useLauncherCosts(30);
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const byOperation = useMemo(
    () => rollup(rows, (r) => `${r.provider} · ${OPERATION_LABELS[r.operation] ?? r.operation}`),
    [rows],
  );
  const byPerson = useMemo(() => rollup(rows, (r) => r.user_upn ?? "(unknown)"), [rows]);
  // Keyed by maker as well: a film's cost is its maker's calls, so two people reusing a label stay apart.
  const byProject = useMemo(
    () => rollup(rows, (r) => `${r.project ?? "(no project label)"} · ${r.user_upn ?? "(unknown)"}`),
    [rows],
  );
  const byDay = useMemo(
    () => rollup(rows, (r) => r.day ?? "").sort((a, b) => b.label.localeCompare(a.label)).slice(0, 7),
    [rows],
  );
  const credits = useMemo(() => rows.reduce((sum, r) => sum + r.provider_credits, 0), [rows]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Launch-video spend</CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          Every ElevenLabs and Gemini call the launch-video MCP tools make over the last 30 days, priced at list rates.
          ElevenLabs figures are pay-as-you-go equivalents; on a subscription they draw down plan credits first.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading spend…</p>}
        {isError && <p className="font-sans text-sm text-bw-ink-muted">Couldn't load launch-video costs.</p>}
        {data && rows.length === 0 && (
          <p className="font-sans text-sm text-bw-ink-muted">No launch-video calls in the last 30 days.</p>
        )}
        {data && rows.length > 0 && (
          <>
            <div className="flex items-baseline gap-2">
              <span className="font-sans text-2xl font-bold text-bw-navy">{usd(data.total_cost_usd)}</span>
              <span className="font-sans text-xs text-bw-ink-muted">
                {data.calls} calls
                {data.errors > 0 && <> · {data.errors} failed</>}
                {credits > 0 && <> · {Math.round(credits).toLocaleString()} ElevenLabs credits reported</>}
                {data.unpriced_calls > 0 && <> · {data.unpriced_calls} on unpriced models, not counted</>}
              </span>
            </div>
            <BucketTable title="By operation" rows={byOperation} latency />
            <BucketTable title="By film" rows={byProject} />
            <BucketTable title="By person" rows={byPerson} />
            <BucketTable title="Last 7 active days" rows={byDay} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
